import { useAuthContext } from '@/auth/useAuthContext';
import { cn } from '@/lib/utils';
import { BarChart3 } from 'lucide-react';
/**
 * Sub-spec 08 Plan 2 §1.3 — staff reports page shell + sidebar + main panel.
 *
 * Layout: 240px sidebar listing the 10 reports grouped Operational / Financial
 * + main panel. Sidebar click → /reports/<slug>. Capability is enforced
 * server-side; the sidebar fetches `/api/reports` which already filters by
 * caller capability so financial reports just don't appear for users who
 * lack `reports.view_financial`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ReportPanel } from './ReportPanel';
import { REPORTS, type ReportRegistryEntry, getReportEntry } from './registry';

interface CatalogueEntry {
  reportType: string;
  title: string;
  capability: 'reports.view_operational' | 'reports.view_financial';
}

export function ReportsPage() {
  const { reportType } = useParams<{ reportType?: string }>();
  const [catalogue, setCatalogue] = useState<CatalogueEntry[] | null>(null);
  const { state } = useAuthContext();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch('/api/reports', { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setCatalogue([]);
          return;
        }
        const body = (await res.json()) as { reports?: CatalogueEntry[] };
        if (!cancelled) setCatalogue(body.reports ?? []);
      } catch {
        if (!cancelled) setCatalogue([]);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const accessibleSlugs = useMemo(() => {
    if (catalogue === null) return new Set<string>();
    return new Set(catalogue.map((c) => c.reportType));
  }, [catalogue]);

  const groups = useMemo(() => {
    const ops = REPORTS.filter((r) => r.group === 'operational' && accessibleSlugs.has(r.slug));
    const fin = REPORTS.filter((r) => r.group === 'financial' && accessibleSlugs.has(r.slug));
    return { ops, fin };
  }, [accessibleSlugs]);

  const selected: ReportRegistryEntry | undefined = reportType
    ? getReportEntry(reportType)
    : undefined;

  const currency = state.me?.activeAccount?.currency ?? 'GBP';
  const timezone = state.me?.activeAccount?.defaultTimezone ?? 'Europe/London';

  return (
    <div className="grid min-h-[100dvh] grid-cols-1 md:grid-cols-[240px_1fr]">
      <aside className="border-b border-border bg-secondary/30 p-4 md:border-b-0 md:border-r">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="size-4 text-primary" strokeWidth={1.75} />
          <h2 className="text-sm font-medium tracking-tight">Reports</h2>
        </div>

        {catalogue === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : catalogue.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You don't have access to any reports. Ask an owner or manager for the
            <span className="font-medium"> reports.view_operational</span> capability.
          </p>
        ) : (
          <nav className="flex flex-col gap-4">
            {groups.ops.length > 0 && (
              <ReportGroup label="Operational" entries={groups.ops} selectedSlug={selected?.slug} />
            )}
            {groups.fin.length > 0 && (
              <ReportGroup label="Financial" entries={groups.fin} selectedSlug={selected?.slug} />
            )}
          </nav>
        )}
      </aside>

      <main className="p-6">
        {!selected ? (
          <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center">
            <BarChart3 className="mx-auto mb-3 size-8 text-muted-foreground" strokeWidth={1.5} />
            <h1 className="text-lg font-medium tracking-tight">Choose a report</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Select a report from the sidebar to see results for your date range.
            </p>
          </div>
        ) : (
          <ReportPanel slug={selected.slug} currency={currency} timezone={timezone} />
        )}
      </main>
    </div>
  );
}

interface GroupProps {
  label: string;
  entries: ReadonlyArray<ReportRegistryEntry>;
  selectedSlug?: string | undefined;
}

function ReportGroup({ label, entries, selectedSlug }: GroupProps) {
  return (
    <div>
      <h3 className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </h3>
      <ul className="flex flex-col gap-0.5">
        {entries.map((entry) => (
          <li key={entry.slug}>
            <Link
              to={`/reports/${entry.slug}`}
              className={cn(
                'block rounded-[9px] px-2.5 py-1.5 text-[13.5px] font-medium tracking-tight press',
                // Tinted (not filled) active treatment — filled primary is
                // reserved for actions; navigation shows selection with a tint.
                selectedSlug === entry.slug
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              {entry.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
